import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import UserLayout from '@/components/Layout/UserLayout.tsx';
import DataTable from '@components/UI/DataTable';
import { showSpinner, hideSpinner } from '@components/Layout/LoadingSpinner';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import type { EntityObjectCompressed, EntityOptionObjectCompressed, TradeTransactionWithFinancials } from '@einhaus/project314-types';
import { sellTradeTypes } from '@einhaus/project314-shared';
import { getTradeOutput } from '@utils/tradingHelpers';
import EntityLink from '@/components/Entity/EntityLink';
import OptionLink from '@/components/Entity/OptionLink';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner.tsx';
import { renderTransactionOutput } from '@/components/Trade/TradeRenderUtils';
import Button from '@/components/UI/Button.tsx';
import { trpc } from '@/api/trpc.ts';
import { handleTrpcQuery } from '@/api/trpcResponseHandler';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useFetchEntities } from '@/hooks/useFetchEntities';
import { useTradingRequisites } from '@/hooks/useTradingRequisites';
import TradeLogicV2ExitSummary from '@/components/TradeLogic/TradeLogicV2ExitSummary';
import { calculateSellToOpenEntryArorc, ENTRY_ARORC_DESCRIPTION, formatArorc } from '@/utils/calculateSellToOpenEntryArorc';
import { formatPositionSide, formatUnderlyingBias, getTradeUnderlyingBiasSummary } from '@/utils/tradeSemantics';

interface TradeRowData {
    id: string;
    name: React.ReactNode;
    direction: string;
    underlyingBias: string;
    isSellToOpen: boolean;
    entryArorc: number | null;
    startingValue: string;
    endingValue: string;
    fees: string;
    changePercent: React.ReactNode;
    profit: React.ReactNode;
    entryDate: string;
    exitDate: string;
    transactions: string | React.ReactNode;
    chart: React.ReactNode;
    // Raw dates for sorting
    entryDateRaw: string;
    exitDateRaw: string;
    // Entity data for the EntityLink component
    entityData?: {
        entityId: number;
        entityType: string;
        entitySymbol: string;
        entityName: string | null;
    } | null;
    // Option data for links
    optionData?: {
        optionId: number;
        description: string;
        startDateTime: string;
        endDateTime: string;
    } | null;
    // Raw transaction data for rendering
    rawTransactions?: TradeTransactionWithFinancials[];
    tradeId: number;
    strategyId: number;
    exitCandidateId: number | null;
    exitCandidateHash: string | null;
}

const PortfolioTradeHistory: React.FC = () => {
    const [error, setError] = useState<string | null>(null);
    const { id } = useParams<{ id: string }>();
    const portfolioId = id ? parseInt(id, 10) : 0;

    // Show loading spinner when component mounts
    useEffect(() => {
        showSpinner();

        // Hide spinner when component unmounts
        return () => {
            hideSpinner();
        };
    }, []);

    // Fetch portfolio data
    const portfolioQuery = useAppQuery(
        {
            ...trpc.user.fetchPortfolio.queryOptions({
                id: portfolioId,
                shouldFetchTrades: true
            }),
            enabled: portfolioId > 0
        },
        {
            fallbackErrorMessage: 'Failed to load portfolio data',
            setError: (msg) => setError(msg)
        }
    );

    // Extract portfolio data
    const portfolio = useMemo(
        () => (portfolioQuery.data?.portfolios && portfolioQuery.data.portfolios.length > 0 ? portfolioQuery.data.portfolios[0] : null),
        [portfolioQuery.data]
    );

    // Fetch trading requisites (strategies, Trade Logic V2 attachments, etc.)
    const requisitesQuery = useTradingRequisites({
        errorConfig: {
            fallbackErrorMessage: 'Failed to fetch trading requisites',
            setError: (msg) => setError(msg)
        }
    });

    // Extract strategies from requisites data
    const strategies = useMemo(() => requisitesQuery.requisites?.tradingStrategies || [], [requisitesQuery.requisites?.tradingStrategies]);

    // Extract trades for stable dependency reference
    const portfolioTrades = portfolio?.trades;

    // Extract entities and options that need to be fetched
    const { entitiesToFetch, optionsToFetch } = useMemo(() => {
        if (!portfolioTrades) {
            return { entitiesToFetch: [], optionsToFetch: [] };
        }

        // Use Maps to ensure uniqueness based on entity ID and type
        const entitiesMap = new Map<string, EntityObjectCompressed>();
        const optionsMap = new Map<string, EntityOptionObjectCompressed>();

        for (const trade of portfolioTrades) {
            const openTransactions = trade.transactions.filter((t) => t.isOpeningTrade);

            if (!openTransactions[0]) continue;

            // Create a unique key for each entity
            const entityKey = `${trade.entityId}_${trade.entityType}`;

            // Only add if not already in the map
            if (!entitiesMap.has(entityKey)) {
                entitiesMap.set(entityKey, {
                    i: trade.entityId,
                    t: trade.entityType === 'Asset' ? 'a' : trade.entityType === 'Cryptocurrency' ? 'c' : 'i'
                });
            }

            for (const openTransaction of openTransactions) {
                if (
                    (trade.entityType === 'Asset' || trade.entityType === 'AssetIndex') &&
                    openTransaction.derivativeType === 'Option' &&
                    openTransaction.derivativeId
                ) {
                    const optionKey = `${openTransaction.derivativeId}_${trade.entityId}_${trade.entityType}`;

                    if (!optionsMap.has(optionKey)) {
                        optionsMap.set(optionKey, {
                            oId: openTransaction.derivativeId,
                            eId: trade.entityId,
                            eType: trade.entityType
                        });
                    }
                }
            }
        }

        // Convert Map values to arrays for the API calls
        return {
            entitiesToFetch: Array.from(entitiesMap.values()),
            optionsToFetch: Array.from(optionsMap.values())
        };
    }, [portfolioTrades]);

    // Fetch entities using the batched hook
    const { entities, isLoading: entitiesLoading } = useFetchEntities(entitiesToFetch);

    // Fetch options
    const optionsQuery = useAppQuery({
        ...trpc.entity.fetchOptions.queryOptions({ options: optionsToFetch }),
        enabled: optionsToFetch.length > 0
    });

    // Calculate loading state
    const loading = useMemo(() => {
        return (
            portfolioQuery.isLoading ||
            requisitesQuery.isLoading ||
            (entitiesToFetch.length > 0 && entitiesLoading) ||
            (optionsToFetch.length > 0 && optionsQuery.isLoading)
        );
    }, [
        portfolioQuery.isLoading,
        requisitesQuery.isLoading,
        entitiesLoading,
        optionsQuery.isLoading,
        entitiesToFetch.length,
        optionsToFetch.length
    ]);

    // Show/hide spinner based on loading state
    useEffect(() => {
        if (loading) {
            showSpinner();
        } else {
            hideSpinner();
        }
    }, [loading]);

    useEffect(() => {
        if (optionsQuery.error && optionsToFetch.length > 0) {
            handleTrpcQuery(optionsQuery, {
                fallbackErrorMessage: 'Failed to load options data',
                setError: (msg) => setError(msg)
            });
        }
    }, [optionsQuery.error, optionsToFetch.length]);

    // Process data for table display (derived state, not useEffect + setState)
    const tableData = useMemo(() => {
        if (!portfolioTrades || (entitiesToFetch.length > 0 && !entities.length)) {
            return [];
        }

        const tradeRows: TradeRowData[] = [];

        for (const trade of portfolioTrades) {
            const openingTransactions = trade.transactions.filter((t) => t.isOpeningTrade);
            if (!openingTransactions[0]) continue;

            const sellTrades = trade.transactions.filter((t) => sellTradeTypes.includes(t.type));
            if (!sellTrades.length) continue;

            const entity = entities.find((e) => e.id === trade.entityId);
            if (!entity) continue;

            const option =
                openingTransactions[0].derivativeType === 'Option' && openingTransactions[0].derivativeId
                    ? optionsQuery.data?.options.find(
                          (o) =>
                              o.id === openingTransactions[0].derivativeId &&
                              o.assetId === trade.entityId &&
                              o.entityType === trade.entityType
                      )
                    : null;

            const tradeOutput = getTradeOutput({
                trade,
                entity,
                option: option ?? null
            });

            if (!tradeOutput) continue;

            const isSellToOpen = openingTransactions.some((transaction) => transaction.type === 'sellToOpen');
            const entryArorc = isSellToOpen
                ? calculateSellToOpenEntryArorc({
                      transactions: openingTransactions,
                      options: optionsQuery.data?.options ?? []
                  })
                : null;

            tradeRows.push({
                id: trade.id.toString(),
                name: tradeOutput.entityLink,
                direction: formatPositionSide(trade.direction),
                underlyingBias: formatUnderlyingBias(getTradeUnderlyingBiasSummary(trade, optionsQuery.data?.options ?? [])),
                isSellToOpen,
                entryArorc,
                startingValue: tradeOutput.startingValue,
                endingValue: tradeOutput.endingValue,
                fees: tradeOutput.fees,
                changePercent: tradeOutput.changeOutput,
                profit: tradeOutput.profitOutput,
                entryDate: tradeOutput.entryDate,
                exitDate: tradeOutput.exitDate,
                transactions: renderTransactionOutput({ transactions: trade.transactions, compactMode: true }),
                chart: (
                    <Button to={`/user/visualizeTrade/${trade.id}`} variant="light">
                        Chart
                    </Button>
                ),
                entryDateRaw: tradeOutput.entryDate || '',
                exitDateRaw: tradeOutput.exitDate || '',
                entityData: {
                    entityId: entity.id,
                    entityType: entity.entityType,
                    entitySymbol: entity.symbol,
                    entityName: entity.name
                },
                optionData: option
                    ? {
                          optionId: option.id,
                          description: option.description,
                          startDateTime: tradeOutput.entryDate || '',
                          endDateTime: tradeOutput.exitDate || ''
                      }
                    : null,
                rawTransactions: trade.transactions,
                tradeId: trade.id,
                strategyId: trade.strategyId,
                exitCandidateId: trade.exitCandidateId ?? null,
                exitCandidateHash: trade.exitCandidateHash ?? null
            });
        }

        return tradeRows;
    }, [portfolioTrades, entities, optionsQuery.data, entitiesToFetch.length]);

    const hasSellToOpenTrades = tableData.some((trade) => trade.isSellToOpen);

    const columns = useMemo<ColumnDef<TradeRowData>[]>(
        () => [
            {
                accessorKey: 'id',
                header: 'ID',
                minSize: 60,
                size: 90,
                maxSize: 120
            },
            {
                accessorKey: 'name',
                header: 'Name',
                minSize: 150,
                size: 250,
                maxSize: 400,
                cell: ({ row }) => {
                    const data = row.original;

                    if (!data.entityData) {
                        return;
                    }

                    // Handle potential null entityName to match Entity type
                    const entity = {
                        entityId: data.entityData.entityId,
                        entityType: data.entityData.entityType,
                        entitySymbol: data.entityData.entitySymbol,
                        entityName: data.entityData.entityName || undefined
                    };

                    return (
                        <div>
                            <EntityLink showName showSymbol entity={entity} />
                            {data.optionData && (
                                <>
                                    <br />
                                    <OptionLink
                                        className="text-zinc-400"
                                        description={data.optionData.description}
                                        entity={{
                                            entityId: data.entityData.entityId,
                                            entityType: data.entityData.entityType
                                        }}
                                        optionId={data.optionData.optionId}
                                    />
                                </>
                            )}
                        </div>
                    );
                }
            },
            {
                accessorKey: 'direction',
                header: 'Position side',
                minSize: 80,
                size: 100,
                maxSize: 150
            },
            {
                accessorKey: 'underlyingBias',
                header: 'Underlying exposure',
                minSize: 115,
                size: 145,
                maxSize: 190
            },
            ...(hasSellToOpenTrades
                ? [
                      {
                          accessorKey: 'entryArorc',
                          header: () => <span title={ENTRY_ARORC_DESCRIPTION}>Entry ARORC</span>,
                          minSize: 80,
                          size: 105,
                          maxSize: 150,
                          cell: ({ row }: { row: { original: TradeRowData } }) => formatArorc(row.original.entryArorc)
                      } satisfies ColumnDef<TradeRowData>
                  ]
                : []),
            {
                accessorKey: 'exitLogic',
                header: 'Exit Rules',
                minSize: 170,
                size: 240,
                maxSize: 320,
                cell: ({ row }) => {
                    const strategy = strategies.find((s) => s.id === row.original.strategyId);

                    return (
                        <TradeLogicV2ExitSummary
                            className="p-2 bg-transparent"
                            direction={row.original.direction}
                            exitCandidateHash={row.original.exitCandidateHash}
                            exitCandidateId={row.original.exitCandidateId}
                            showCandidateInfo={false}
                            showDisplay={false}
                            strategy={strategy}
                        />
                    );
                }
            },
            {
                accessorKey: 'startingValue',
                header: 'Starting Value',
                minSize: 100,
                size: 150,
                maxSize: 200
            },
            {
                accessorKey: 'endingValue',
                header: 'Ending Value',
                minSize: 100,
                size: 150,
                maxSize: 200
            },
            {
                accessorKey: 'fees',
                header: 'Fees',
                minSize: 60,
                size: 100,
                maxSize: 150
            },
            {
                accessorKey: 'changePercent',
                header: '% Change',
                minSize: 80,
                size: 125,
                maxSize: 180,
                cell: ({ row }) => <div>{row.original.changePercent}</div>
            },
            {
                accessorKey: 'profit',
                header: 'Profit',
                minSize: 80,
                size: 125,
                maxSize: 180,
                cell: ({ row }) => <div>{row.original.profit}</div>
            },
            {
                accessorKey: 'entryDate',
                header: 'Entry Date',
                minSize: 100,
                size: 150,
                maxSize: 200,
                sortFn: (rowA, rowB) => {
                    return new Date(rowA.original.entryDateRaw).getTime() - new Date(rowB.original.entryDateRaw).getTime();
                }
            },
            {
                accessorKey: 'exitDate',
                header: 'Exit Date',
                minSize: 100,
                size: 150,
                maxSize: 200,
                sortFn: (rowA, rowB) => {
                    return new Date(rowA.original.exitDateRaw).getTime() - new Date(rowB.original.exitDateRaw).getTime();
                }
            },
            {
                accessorKey: 'transactions',
                header: 'Transactions',
                minSize: 150,
                size: 250,
                maxSize: 400,
                cell: ({ row }) => {
                    const data = row.original;

                    // If it's already a React element, return it directly
                    if (typeof data.transactions !== 'string') {
                        return data.transactions;
                    }

                    // If we have raw transactions, use renderTransactionOutput
                    if (data.rawTransactions && data.rawTransactions.length > 0) {
                        return (
                            <div className="transaction-column wrap-break-word">
                                {renderTransactionOutput({ transactions: data.rawTransactions })}
                            </div>
                        );
                    }
                }
            },
            {
                accessorKey: 'chart',
                header: 'Chart',
                minSize: 80,
                size: 100,
                maxSize: 150,
                cell: ({ row }): React.ReactNode => row.original.chart
            }
        ],
        [hasSellToOpenTrades, strategies]
    );

    return (
        <UserLayout
            containerSize="1800px"
            title={
                loading
                    ? portfolio?.name
                        ? `${portfolio.name} - Trade History - Project 314`
                        : 'Trade History - Project 314'
                    : error
                      ? portfolio?.name
                          ? `${portfolio.name} - Trade History - Error - Project 314`
                          : 'Trade History - Error - Project 314'
                      : portfolio?.name
                        ? `${portfolio.name} - Trade History - Project 314`
                        : 'Trade History - Project 314'
            }
        >
            {loading ? (
                <DataLoadingSpinner />
            ) : error ? (
                <div className="container mx-auto">
                    <div className="bg-red-100 text-red-800 border border-red-200 p-4 mb-4 rounded-md">{error}</div>
                </div>
            ) : (
                <div className="container mx-auto">
                    <div className="flex justify-between mb-4">
                        <div>
                            <h3 className="text-white mb-3 text-xl font-semibold">
                                {portfolio ? (
                                    <>
                                        <Link className="text-primary hover:text-blue-400" to={`/user/portfolio/${portfolio.id}`}>
                                            {portfolio.name}
                                        </Link>{' '}
                                        - Trade History
                                    </>
                                ) : (
                                    'Portfolio Trade History'
                                )}
                            </h3>
                        </div>
                    </div>

                    <div className="p-4 rounded-lg shadow-lg">
                        <DataTable search sort striped columns={columns} data={tableData} pagination={{ enabled: true, pageSize: 50 }} />
                    </div>
                </div>
            )}
        </UserLayout>
    );
};

export default PortfolioTradeHistory;
